import { useEffect, useRef, useState } from 'react';
import { Camera, FileUp, XCircle } from 'lucide-react';
import { useComprobanteScan } from '../hooks/useComprobanteScan.js';

const COMPROBANTE_FIELDS = [
  { key: 'fecha', label: 'Fecha' },
  { key: 'importe', label: 'Importe' },
  { key: 'medioPago', label: 'Medio de pago' },
  { key: 'referencia', label: 'Referencia' },
  { key: 'banco', label: 'Banco / Cuenta' },
  { key: 'cuit', label: 'CUIT' },
  { key: 'clienteNombre', label: 'Cliente' },
];

const CHEQUE_FIELDS = [
  { key: 'numeroCheque', label: 'Número de cheque' },
  { key: 'banco', label: 'Banco' },
  { key: 'importe', label: 'Importe' },
  { key: 'fechaEmision', label: 'Fecha de emisión' },
  { key: 'fechaCobro', label: 'Fecha de pago / vencimiento' },
  { key: 'cuit', label: 'CUIT titular' },
];

function formatValue(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'number') {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value);
  }
  return value;
}

export default function ComprobanteScanModal({ isOpen, onClose, sucursalId, onApply, purpose = 'comprobante' }) {
  const { loading, result, error, scan, reset } = useComprobanteScan();
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const [selected, setSelected] = useState({});
  const [batchResults, setBatchResults] = useState([]);
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const isCheque = purpose === 'cheque';
  const fields = isCheque ? CHEQUE_FIELDS : COMPROBANTE_FIELDS;

  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!result?.suggested) return;
    const nextSelected = {};
    fields.forEach((field) => {
      nextSelected[field.key] = Boolean(result.suggested?.[field.key]?.value);
    });
    setSelected(nextSelected);
  }, [fields, result]);

  const scanResults = isCheque ? batchResults : (result ? [{ file, result, selected }] : []);
  const hasResults = scanResults.length > 0;

  if (!isOpen) return null;

  const handleSelectFile = (nextFile) => {
    if (!nextFile) return;
    setFile(nextFile);
    setFiles([nextFile]);
    setSelected({});
    setBatchResults([]);
    reset();
  };

  const handleSelectChequeFiles = (nextFiles) => {
    const selectedFiles = Array.from(nextFiles || []).filter((item) => item.type === 'image/jpeg' || item.type === 'image/png').slice(0, 6);
    if (!selectedFiles.length) return;
    setFiles(selectedFiles);
    setFile(selectedFiles[0]);
    setSelected({});
    setBatchResults([]);
    reset();
  };

  const handleScan = async () => {
    if (isCheque) {
      const nextResults = [];
      for (const chequeFile of files) {
        const scanned = await scan({ file: chequeFile, sucursalId });
        if (!scanned) break;
        const selectedFields = {};
        fields.forEach((field) => { selectedFields[field.key] = Boolean(scanned.suggested?.[field.key]?.value); });
        nextResults.push({ file: chequeFile, result: scanned, selected: selectedFields });
      }
      setBatchResults(nextResults);
      return;
    }
    await scan({ file, sucursalId });
  };

  const handleApply = () => {
    if (isCheque) {
      const payload = batchResults.map(({ result: scanned, selected: selectedFields }) => fields.reduce((values, field) => {
        if (selectedFields[field.key]) values[field.key] = scanned.suggested?.[field.key]?.value ?? null;
        return values;
      }, {}));
      onApply?.(payload, batchResults);
      return;
    }
    const payload = {};
    fields.forEach((field) => {
      if (selected[field.key]) payload[field.key] = result?.suggested?.[field.key]?.value ?? null;
    });
    onApply?.(payload, result);
  };

  const toggleBatchField = (resultIndex, fieldKey, checked) => {
    setBatchResults((current) => current.map((item, index) => index === resultIndex ? {
      ...item,
      selected: { ...item.selected, [fieldKey]: checked },
    } : item));
  };

  const title = isCheque ? 'Cargar cheque con foto' : 'Leer comprobante';
  const description = isCheque
    ? 'Elegí cómo cargar el cheque. El OCR propone los datos y podés corregirlos antes de registrar la cobranza.'
    : 'Cargá una imagen o PDF para sugerir campos del formulario.';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="comprobante-scan-title"
        className="w-full max-w-3xl rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)]"
      >
        <div className="flex items-start justify-between border-b border-[rgb(var(--border))] px-6 py-4">
          <div>
            <h2 id="comprobante-scan-title" className="text-lg font-semibold text-[rgb(var(--fg))]">{title}</h2>
            <p className="text-sm text-[rgb(var(--muted))]">{description}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))]" aria-label="Cerrar">
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 space-y-3 md:col-span-5">
              {isCheque ? (
                <>
                  <p className="text-sm font-medium text-[rgb(var(--fg))]">¿Cómo querés cargarlo?</p>
                  <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-1">
                    <button type="button" onClick={() => cameraInputRef.current?.click()} className="flex items-center justify-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2.5 text-sm font-semibold text-white">
                      <Camera className="h-4 w-4" /> Abrir cámara
                    </button>
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center justify-center gap-2 rounded-xl border border-[rgb(var(--border))] px-4 py-2.5 text-sm font-semibold text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.15)]">
                      <FileUp className="h-4 w-4" /> Seleccionar archivo
                    </button>
                  </div>
                  <input ref={cameraInputRef} className="sr-only" type="file" accept="image/png,image/jpeg" capture="environment" onChange={(event) => handleSelectChequeFiles(event.target.files)} />
                  <input ref={fileInputRef} className="sr-only" type="file" accept="image/png,image/jpeg" multiple onChange={(event) => handleSelectChequeFiles(event.target.files)} />
                </>
              ) : (
                <>
                  <label className="block text-sm font-medium text-[rgb(var(--fg))]" htmlFor="scan-file">Archivo</label>
                  <input id="scan-file" type="file" accept="image/png,image/jpeg,application/pdf" onChange={(event) => handleSelectFile(event.target.files?.[0])} className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-3 py-2 text-sm text-[rgb(var(--fg))] file:mr-4 file:rounded-lg file:border-0 file:bg-[rgb(var(--brand))] file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-white" />
                </>
              )}
              {file ? <p className="text-xs text-[rgb(var(--muted))]">{isCheque && files.length > 1 ? `${files.length} cheques seleccionados (máximo 6 por tanda).` : `Seleccionado: ${file.name}`}</p> : null}
              <button type="button" disabled={!file || loading || !sucursalId} onClick={handleScan} className="w-full rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {loading ? 'Analizando...' : isCheque ? `Leer ${files.length > 1 ? `${files.length} cheques` : 'datos del cheque'}` : 'Analizar'}
              </button>
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
            <div className="col-span-12 md:col-span-7">
              <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel))] p-3">
                {previewUrl ? (
                  file?.type === 'application/pdf' ? <div className="text-sm text-[rgb(var(--fg))]">PDF seleccionado: <span className="font-semibold">{file.name}</span></div> : <img src={previewUrl} alt="Vista previa del documento" className="max-h-72 w-full rounded-xl object-contain" />
                ) : <div className="text-sm text-[rgb(var(--muted))]">Sin archivo seleccionado.</div>}
              </div>
              {result?.warnings?.length ? <div className="mt-3 space-y-1">{result.warnings.map((warning, index) => <p key={`${warning}-${index}`} className="text-xs text-amber-500">{warning}</p>)}</div> : null}
            </div>
          </div>

          <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel))] p-4">
            <div className="mb-3 flex items-center justify-between"><h3 className="text-sm font-semibold text-[rgb(var(--fg))]">Campos detectados</h3><p className="text-xs text-[rgb(var(--muted))]">Elegí qué aplicar; siempre podés editarlo después.</p></div>
            {!hasResults ? <p className="text-sm text-[rgb(var(--muted))]">Esperando análisis del documento.</p> : isCheque ? (
              <div className="space-y-4">{batchResults.map((item, resultIndex) => <section key={`${item.file.name}-${resultIndex}`} className="rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] p-3"><p className="mb-2 text-sm font-semibold text-[rgb(var(--fg))]">Cheque {resultIndex + 1}: {item.file.name}</p><div className="space-y-2">{fields.map((field) => { const data = item.result.suggested?.[field.key]; return <div key={field.key} className="flex flex-wrap items-center justify-between gap-3"><div><p className="text-xs uppercase text-[rgb(var(--muted))]">{field.label}</p><p className="text-sm text-[rgb(var(--fg))]">{formatValue(data?.value)}</p></div><div className="flex items-center gap-4"><span className="text-xs text-[rgb(var(--muted))]">Confianza {data?.confidence ?? 0}%</span><label className="flex items-center gap-2 text-xs text-[rgb(var(--fg))]"><input type="checkbox" checked={Boolean(item.selected[field.key])} onChange={(event) => toggleBatchField(resultIndex, field.key, event.target.checked)} className="h-4 w-4 rounded border-[rgb(var(--border))] text-[rgb(var(--brand))]" />Aplicar</label></div></div>; })}</div></section>)}</div>
            ) : (
              <div className="space-y-3">{fields.map((field) => {
                const data = result?.suggested?.[field.key];
                return <div key={field.key} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-4 py-2"><div><p className="text-xs uppercase text-[rgb(var(--muted))]">{field.label}</p><p className="text-sm text-[rgb(var(--fg))]">{formatValue(data?.value)}</p></div><div className="flex items-center gap-4"><span className="text-xs text-[rgb(var(--muted))]">Confianza {data?.confidence ?? 0}%</span><label className="flex items-center gap-2 text-xs text-[rgb(var(--fg))]"><input type="checkbox" checked={Boolean(selected[field.key])} onChange={(event) => setSelected((prev) => ({ ...prev, [field.key]: event.target.checked }))} className="h-4 w-4 rounded border-[rgb(var(--border))] text-[rgb(var(--brand))]" />Aplicar</label></div></div>;
              })}</div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-[rgb(var(--border))] px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)]">Cerrar</button>
          <button type="button" disabled={!hasResults} onClick={handleApply} className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">Aplicar al formulario</button>
        </div>
      </div>
    </div>
  );
}
